import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Supports original variant metafield text and optional
 * variant JSON text selected by the URL region.
 */
class VariantMetafieldText extends Component {
  #section;
  #observer;
  #scheduled = false;
  #region = null;
  #lastContent = null;
  #lastSignature = null;

  connectedCallback() {
    super.connectedCallback();

    this.#rememberRegion();

    this.#section = this.closest('.shopify-section, dialog');

    this.#section?.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    // Only the new JSON option needs regional initialization.
    if (this.dataset.textSource === 'variant_json_region') {
      this.#observer = new MutationObserver(() => {
        this.#scheduleRegionalText();
      });

      this.#observer.observe(this, {
        childList: true,
        subtree: true,
        characterData: true
      });

      this.#scheduleRegionalText();
    }
  }

  disconnectedCallback() {
    this.#observer?.disconnect();

    this.#section?.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.#lastContent = null;
    this.#lastSignature = null;

    super.disconnectedCallback();
  }

  #rememberRegion() {
    const region = new URLSearchParams(
      window.location.search
    ).get('region');

    if (region && /^[1-5]$/.test(region)) {
      this.#region = region;
    }

    // Retain the initial region if a variant update removes
    // it from the URL.
  }

  #scheduleRegionalText() {
    if (this.#scheduled) return;

    this.#scheduled = true;

    queueMicrotask(() => {
      this.#scheduled = false;

      if (this.isConnected) {
        this.#applyRegionalText();
      }
    });
  }

  #applyRegionalText() {
    if (this.dataset.textSource !== 'variant_json_region') {
      return;
    }

    this.#rememberRegion();

    const content = this.querySelector(
      '[data-variant-metafield-content]'
    );

    if (!content) return;

    const jsonElement = this.querySelector(
      'script[data-variant-region-json]'
    );

    const rawJSON = jsonElement?.textContent.trim() || '';

    let lines = [];

    if (this.#region && rawJSON) {
      try {
        const data = JSON.parse(rawJSON);

        if (
          data &&
          typeof data === 'object' &&
          !Array.isArray(data)
        ) {
          const value = data[this.#region];

          const entries = Array.isArray(value)
            ? value
            : typeof value === 'string'
              ? [value]
              : [];

          if (
            entries.every((entry) => typeof entry === 'string')
          ) {
            lines = entries
              .map((entry) => entry.trim())
              .filter(Boolean);
          }
        }
      } catch (error) {
        console.warn(
          '[variant-metafield-text] Invalid regional JSON:',
          error
        );
      }
    }

    const signature = JSON.stringify([
      this.#region,
      rawJSON,
      lines
    ]);

    // Prevent our own DOM updates from causing a render loop.
    if (
      this.#lastContent === content &&
      this.#lastSignature === signature
    ) {
      return;
    }

    this.#lastContent = content;
    this.#lastSignature = signature;

    const fragment = document.createDocumentFragment();

    for (const line of lines) {
      const paragraph = document.createElement('p');

      // Render JSON values as plain text, never HTML.
      paragraph.textContent = line;

      fragment.appendChild(paragraph);
    }

    content.replaceChildren(fragment);

    const isEmpty = lines.length === 0;

    content.hidden = isEmpty;
    this.hidden = isEmpty;
  }

  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    this.#rememberRegion();

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || !detail?.html) return;

        if (
          detail.productId &&
          this.dataset.productId &&
          String(detail.productId) !== this.dataset.productId
        ) {
          return;
        }

        const blockId = this.dataset.blockId;

        if (!blockId) return;

        const newComponent = detail.html.querySelector(
          `variant-metafield-text[data-block-id="${CSS.escape(blockId)}"]`
        );

        const currentContent = this.querySelector(
          '[data-variant-metafield-content]'
        );

        if (!currentContent) return;

        /*
         * New option: update the selected variant's JSON,
         * then render the current region.
         */
        if (this.dataset.textSource === 'variant_json_region') {
          const oldJSON = this.querySelector(
            'script[data-variant-region-json]'
          );

          const newJSON = newComponent?.querySelector(
            'script[data-variant-region-json]'
          );

          oldJSON?.remove();

          if (newJSON) {
            this.appendChild(newJSON.cloneNode(true));
          }

          this.#lastContent = null;
          this.#lastSignature = null;

          this.#applyRegionalText();
          return;
        }

        /*
         * Original option: preserve the original
         * variant metafield update behavior.
         */
        const newContent = newComponent?.querySelector(
          '[data-variant-metafield-content]'
        );

        if (
          newContent &&
          newContent.innerHTML.trim() !== ''
        ) {
          currentContent.innerHTML = newContent.innerHTML;
          currentContent.hidden = false;
          this.hidden = false;
        } else {
          currentContent.innerHTML = '';
          currentContent.hidden = true;
          this.hidden = true;
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[variant-metafield-text] Event promise rejected:',
            error
          );
        }
      });
  };
}

if (!customElements.get('variant-metafield-text')) {
  customElements.define(
    'variant-metafield-text',
    VariantMetafieldText
  );
}